import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Plus,
  Copy,
  Edit3,
  Layers,
  Hammer,
  Package,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Trash2,
  Sparkles,
  Scale,
  X,
  Loader2,
  ShieldCheck,
  Wrench
} from 'lucide-react';
import {
  PTFurnitureItem,
  BOMRecipeLine,
  BOMMaterialRef,
  PTCatalogOverviewResponse,
  PT_FAMILIES,
  PT_UNITS,
  PT_STATUSES,
  PT_ORIGINS,
  PT_FIXATION_LOADS,
  BOM_PRODUCTION_STAGES
} from '../types/crm';

interface PTCatalogAndBOMBuilderTabProps {
  token?: string | null;
  onNotify?: (type: 'success' | 'error', text: string) => void;
}

export const PTCatalogAndBOMBuilderTab: React.FC<PTCatalogAndBOMBuilderTabProps> = ({
  token,
  onNotify
}) => {
  const [data, setData] = useState<PTCatalogOverviewResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Filtros reactivos
  const [searchTerm, setSearchTerm] = useState('');
  const [familyFilter, setFamilyFilter] = useState<string>('TODAS');
  const [modeFilter, setModeFilter] = useState<
    'ALL' | 'WITH_BOM' | 'WITHOUT_BOM' | 'KITS_4UP' | 'FIXATION' | 'PARAMETRIC'
  >('ALL');
  const [visibleLimit, setVisibleLimit] = useState<number>(36);

  // Modal / Drawer de Inspección, Edición y Clonación de Receta BOM
  const [bomModal, setBomModal] = useState<{
    open: boolean;
    mode: 'inspect_edit' | 'clone_new';
    baseItem: PTFurnitureItem | null;
  }>({
    open: false,
    mode: 'inspect_edit',
    baseItem: null
  });

  // Formulario del Mueble Destino (para clone_new o edición de ficha PT)
  const [ptEditorModal, setPtEditorModal] = useState<{
    open: boolean;
    action: 'create_pt' | 'update_pt';
    id?: string;
  }>({ open: false, action: 'create_pt' });

  const [ptForm, setPtForm] = useState({
    codigo: '',
    nombre: '',
    familia: 'Carpintería',
    unidadMedida: 'UND',
    estatus: 'Activo',
    origenRegistro: 'Taller / Sanesca STD',
    esKitModular: false,
    subKitEnsamble: '',
    requiereFijacion: false,
    tipoCargaFijacion: '⚪ Sin Fijación / Directo',
    puntosPared: 0,
    puntosPiso: 0,
    empaqueNominal: 1
  });

  // Estado editable de líneas BOM en el Constructor/Clonador
  const [editableBomLines, setEditableBomLines] = useState<BOMRecipeLine[]>([]);
  const [cloneSourceCode, setCloneSourceCode] = useState<string>('');
  const [templateSearch, setTemplateSearch] = useState<string>('');
  const [matSearch, setMatSearch] = useState<string>('');
  const [scaleFactor, setScaleFactor] = useState<string>('1');
  const [newMatCode, setNewMatCode] = useState<string>('');
  const [newMatQty, setNewMatQty] = useState<string>('1');
  const [newMatMerma, setNewMatMerma] = useState<string>('0');
  const [newMatStage, setNewMatStage] = useState<string>('Carpintería / Estructura');
  const [supervisorPIN, setSupervisorPIN] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const notify = (type: 'success' | 'error', text: string) => {
    if (onNotify) onNotify(type, text);
  };

  const fetchCatalog = async (force = false) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await fetch(`/api/bom/catalog${force ? '?force=1' : ''}`);
      const json: PTCatalogOverviewResponse = await res.json();
      if (!res.ok || json.error) {
        throw new Error(json.error || `Error HTTP ${res.status}`);
      }
      setData(json);
    } catch (err: any) {
      setErrorMsg(err.message || 'Error cargando Catálogo PT y Recetas BOM.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalog(false);
  }, []);

  useEffect(() => {
    setVisibleLimit(36);
  }, [searchTerm, familyFilter, modeFilter]);

  const items = data?.items || [];
  const materialsCatalog = data?.materialsCatalog || [];

  const materialsByCode = useMemo(() => {
    const map = new Map<string, BOMMaterialRef>();
    for (const m of materialsCatalog) {
      map.set(m.code.toUpperCase(), m);
    }
    return map;
  }, [materialsCatalog]);

  const itemsWithBom = useMemo(() => items.filter(i => i.hasBOM), [items]);

  const filteredTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    if (!q) return itemsWithBom;
    return itemsWithBom.filter(
      it => it.codigo.toLowerCase().includes(q) || it.nombre.toLowerCase().includes(q)
    );
  }, [itemsWithBom, templateSearch]);

  const filteredMaterialsCatalog = useMemo(() => {
    const q = matSearch.trim().toLowerCase();
    if (!q) return materialsCatalog;
    return materialsCatalog.filter(
      m =>
        m.code.toLowerCase().includes(q) ||
        m.sku.toLowerCase().includes(q) ||
        m.nombre.toLowerCase().includes(q)
    );
  }, [materialsCatalog, matSearch]);

  const filteredItems = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return items.filter(item => {
      if (familyFilter !== 'TODAS' && item.familia !== familyFilter) return false;
      if (modeFilter === 'WITH_BOM' && !item.hasBOM) return false;
      if (modeFilter === 'WITHOUT_BOM' && item.hasBOM) return false;
      if (
        modeFilter === 'KITS_4UP' &&
        !Boolean(item.isTrueCompositeKit || (item.subKitEnsamble && item.subKitEnsamble.trim().length > 0))
      ) {
        return false;
      }
      if (modeFilter === 'FIXATION' && !item.requiereFijacion) return false;
      if (
        modeFilter === 'PARAMETRIC' &&
        !['METROS', 'ML', 'MT2', 'M2', 'GL', 'SERV'].includes((item.unidadMedida || '').toUpperCase()) &&
        !item.codigo.toUpperCase().startsWith('SERV-')
      ) {
        return false;
      }

      if (!q) return true;
      return (
        item.codigo.toLowerCase().includes(q) ||
        item.nombre.toLowerCase().includes(q) ||
        item.familia.toLowerCase().includes(q) ||
        item.bomLines.some(
          l =>
            l.nombre.toLowerCase().includes(q) ||
            l.matCode.toLowerCase().includes(q) ||
            (l.sku && l.sku.toLowerCase().includes(q))
        )
      );
    });
  }, [items, familyFilter, modeFilter, searchTerm]);

  // Generador asistido de desglose 4-UP (<= 80 caracteres por línea)
  const buildSuggested4UPBreakdown = (codigo: string, nombre: string): string => {
    const existingItem = items.find(i => i.codigo === codigo);
    if (existingItem?.suggestedSubKit4UP) return existingItem.suggestedSubKit4UP;
    const upper = String(nombre || '').toUpperCase().trim();
    if (!upper) return '';
    const clamp80 = (s: string) => {
      const clean = s.replace(/\s+/g, ' ').trim();
      return clean.length <= 80 ? clean : clean.slice(0, 77).trim() + '...';
    };
    if (upper.includes('CON AUXILIAR')) {
      const parts = upper.split(/\s+CON\s+AUXILIAR\s*/i);
      return [
        clamp80(`1/2 ${codigo}: ${parts[0] || upper}`),
        clamp80(`2/2 ${codigo}: MODULO AUXILIAR ${parts[1] || 'COMPLEMENTARIO'}`)
      ].join('\n');
    }
    if (/JUEGO\s+TRIPLE|TRIPLE\s+DE\s+MESAS|SET\s+DE\s+3/i.test(upper)) {
      const core = upper.replace(/^JUEGO\s+TRIPLE\s+DE\s+|^SET\s+DE\s+3\s+/i, '');
      return [
        clamp80(`1/3 ${codigo}: UNIDAD GRANDE - ${core}`),
        clamp80(`2/3 ${codigo}: UNIDAD MEDIANA - ${core}`),
        clamp80(`3/3 ${codigo}: UNIDAD PEQUENA - ${core}`)
      ].join('\n');
    }
    return [
      clamp80(`1/2 ${codigo}: MODULO PRINCIPAL - ${upper}`),
      clamp80(`2/2 ${codigo}: COMPONENTE SECUNDARIO / KIT ENSAMBLE`)
    ].join('\n');
  };

  // Validación en vivo del desglose 4-UP (<= 80 caracteres por línea)
  const subKitValidation = useMemo(() => {
    const raw = ptForm.subKitEnsamble || '';
    if (!raw.trim()) return { valid: true, lines: [] as { text: string; len: number; ok: boolean }[] };
    const lines = raw
      .split(/\r?\n|;/)
      .map(s => s.trim())
      .filter(Boolean)
      .map(text => ({
        text,
        len: text.length,
        ok: text.length <= 80
      }));
    return {
      valid: lines.every(l => l.ok),
      lines
    };
  }, [ptForm.subKitEnsamble]);

  // Abrir Inspector / Editor de Receta BOM de un mueble existente
  const openInspectEditBOM = (item: PTFurnitureItem) => {
    setEditableBomLines(item.bomLines.map(l => ({ ...l })));
    setCloneSourceCode('');
    setTemplateSearch('');
    setMatSearch('');
    setScaleFactor('1');
    setBomModal({
      open: true,
      mode: 'inspect_edit',
      baseItem: item
    });
  };

  // Abrir Clonador de Mueble + Receta BOM (para variante a medida)
  const openCloneFurnitureWithBOM = (sourceItem: PTFurnitureItem) => {
    const cleanCode = sourceItem.codigo.replace(/\*+$/, '');
    setPtForm({
      codigo: `${cleanCode}-ESP`,
      nombre: `${sourceItem.nombre} (A MEDIDA)`,
      familia: sourceItem.familia || 'Carpintería',
      unidadMedida: sourceItem.unidadMedida || 'UND',
      estatus: 'Activo',
      origenRegistro: 'Taller / Sanesca STD',
      esKitModular: Boolean(sourceItem.isTrueCompositeKit || sourceItem.esKitModular),
      subKitEnsamble: sourceItem.subKitEnsamble || sourceItem.suggestedSubKit4UP || '',
      requiereFijacion: sourceItem.requiereFijacion,
      tipoCargaFijacion: sourceItem.tipoCargaFijacion || '⚪ Sin Fijación / Directo',
      puntosPared: sourceItem.puntosPared || 0,
      puntosPiso: sourceItem.puntosPiso || 0,
      empaqueNominal: sourceItem.empaqueNominal || 1
    });
    setEditableBomLines(sourceItem.bomLines.map(l => ({ ...l, source: 'kv_custom' })));
    setCloneSourceCode(sourceItem.codigo);
    setTemplateSearch('');
    setMatSearch('');
    setScaleFactor('1');
    setBomModal({
      open: true,
      mode: 'clone_new',
      baseItem: sourceItem
    });
  };

  // Abrir Editor de Ficha PT y 4-UP
  const openCreatePT = () => {
    setPtForm({
      codigo: `P${items.length + 100}-STD`,
      nombre: '',
      familia: 'Carpintería',
      unidadMedida: 'UND',
      estatus: 'Activo',
      origenRegistro: 'Taller / Sanesca STD',
      esKitModular: false,
      subKitEnsamble: '',
      requiereFijacion: false,
      tipoCargaFijacion: '⚪ Sin Fijación / Directo',
      puntosPared: 0,
      puntosPiso: 0,
      empaqueNominal: 1
    });
    setPtEditorModal({ open: true, action: 'create_pt' });
  };

  const openEditPT = (item: PTFurnitureItem) => {
    setPtForm({
      codigo: item.codigo,
      nombre: item.nombre,
      familia: item.familia || 'Carpintería',
      unidadMedida: item.unidadMedida || 'UND',
      estatus: item.estatus || 'Activo',
      origenRegistro: item.origenRegistro || 'Valery ERP (Oficial)',
      esKitModular: Boolean(item.isTrueCompositeKit || item.esKitModular),
      subKitEnsamble: item.subKitEnsamble || item.suggestedSubKit4UP || '',
      requiereFijacion: item.requiereFijacion,
      tipoCargaFijacion: item.tipoCargaFijacion || '⚪ Sin Fijación / Directo',
      puntosPared: item.puntosPared || 0,
      puntosPiso: item.puntosPiso || 0,
      empaqueNominal: item.empaqueNominal || 1
    });
    setPtEditorModal({ open: true, action: 'update_pt', id: item.id });
  };

  // Cargar plantilla BOM desde otro mueble del catálogo
  const handleLoadRecipeFromTemplate = (templateCode: string) => {
    setCloneSourceCode(templateCode);
    const found = items.find(i => i.codigo === templateCode);
    if (!found) return;
    setEditableBomLines(
      found.bomLines.map(l => ({
        ...l,
        source: 'kv_custom'
      }))
    );
  };

  // Escalar cantidades base por multiplicador dimensional (ej. 1.25x)
  const handleApplyScaleFactor = () => {
    const factor = Number(scaleFactor);
    if (!factor || factor <= 0) return;
    setEditableBomLines(prev =>
      prev.map(l => {
        const newQty = Number((l.cantidadBase * factor).toFixed(4));
        const subtotalUSD = Number((newQty * (1 + (l.mermaPct || 0) / 100) * l.costoUnitarioUSD).toFixed(2));
        return {
          ...l,
          cantidadBase: newQty,
          subtotalUSD,
          source: 'kv_custom'
        };
      })
    );
  };

  const handleUpdateBomLine = (idx: number, field: 'cantidadBase' | 'mermaPct' | 'etapaProductiva', value: any) => {
    setEditableBomLines(prev =>
      prev.map((line, i) => {
        if (i !== idx) return line;
        const updated = {
          ...line,
          [field]: field === 'etapaProductiva' ? value : Number(value) || 0,
          source: 'kv_custom' as const
        };
        updated.subtotalUSD = Number(
          (updated.cantidadBase * (1 + (updated.mermaPct || 0) / 100) * updated.costoUnitarioUSD).toFixed(2)
        );
        return updated;
      })
    );
  };

  const handleRemoveBomLine = (idx: number) => {
    setEditableBomLines(prev => prev.filter((_, i) => i !== idx));
  };

  const handleAddMaterialToBom = () => {
    if (!newMatCode) return;
    const matRef = materialsByCode.get(newMatCode.toUpperCase());
    if (!matRef) return;
    const cant = Number(newMatQty) || 1;
    const merma = Number(newMatMerma) || 0;
    const subtotalUSD = Number((cant * (1 + merma / 100) * matRef.costoUSD).toFixed(2));

    setEditableBomLines(prev => [
      ...prev,
      {
        matCode: matRef.code,
        sku: matRef.sku,
        insumoNotionId: matRef.notionId,
        nombre: matRef.nombre,
        unidad: matRef.unidad,
        cantidadBase: cant,
        mermaPct: merma,
        etapaProductiva: newMatStage,
        costoUnitarioUSD: matRef.costoUSD,
        subtotalUSD,
        source: 'kv_custom'
      }
    ]);
    setNewMatCode('');
    setNewMatQty('1');
    setNewMatMerma('0');
  };

  const totalEditableBomCostUSD = useMemo(() => {
    return Number(editableBomLines.reduce((acc, l) => acc + (l.subtotalUSD || 0), 0).toFixed(2));
  }, [editableBomLines]);

  const getAuthHeaders = () => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  // Guardar Ficha PT (create_pt / update_pt)
  const handleSavePTForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subKitValidation.valid) {
      notify('error', 'Corrige las líneas del SubKit 4-UP que superan los 80 caracteres.');
      return;
    }
    setIsSaving(true);
    try {
      const res = await fetch('/api/bom/upsert', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action: ptEditorModal.action,
          id: ptEditorModal.id,
          ptData: ptForm,
          supervisorPIN: supervisorPIN || undefined
        })
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        throw new Error(json.error || 'Error guardando Ficha de Mueble PT.');
      }
      setPtEditorModal({ open: false, action: 'create_pt' });
      notify('success', `Mueble PT "${ptForm.codigo}" sincronizado en Notion ERP.`);
      await fetchCatalog(true);
    } catch (err: any) {
      notify('error', err.message || 'Fallo al guardar Ficha PT.');
    } finally {
      setIsSaving(false);
    }
  };

  // Guardar Receta BOM o Clonación Completa (save_bom_recipe / clone_with_bom)
  const handleSaveBomModal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editableBomLines.length === 0) {
      notify('error', 'La receta BOM debe contener al menos 1 insumo.');
      return;
    }
    setIsSaving(true);
    try {
      const isClone = bomModal.mode === 'clone_new';
      const payload = isClone
        ? {
            action: 'clone_with_bom',
            ptData: ptForm,
            bomLines: editableBomLines,
            supervisorPIN: supervisorPIN || undefined
          }
        : {
            action: 'save_bom_recipe',
            id: bomModal.baseItem?.id,
            codigo: bomModal.baseItem?.codigo,
            ptData: {
              id: bomModal.baseItem?.id,
              codigo: bomModal.baseItem?.codigo,
              nombre: bomModal.baseItem?.nombre
            },
            bomLines: editableBomLines,
            supervisorPIN: supervisorPIN || undefined
          };

      const res = await fetch('/api/bom/upsert', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        throw new Error(json.error || 'Error guardando Receta BOM en Notion ERP.');
      }
      setBomModal({ open: false, mode: 'inspect_edit', baseItem: null });
      notify(
        'success',
        isClone
          ? `Mueble clonado "${ptForm.codigo}" creado con ${editableBomLines.length} insumos BOM.`
          : `Receta BOM de "${bomModal.baseItem?.codigo}" actualizada (${editableBomLines.length} insumos).`
      );
      await fetchCatalog(true);
    } catch (err: any) {
      notify('error', err.message || 'Fallo al guardar Receta BOM.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Sub-Header KPI Strip del Catálogo PT y BOM */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <div className="bg-page border border-borderSubtle rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Catálogo Mobiliario PT
            </p>
            <p className="text-lg font-black text-white mt-0.5">
              {data?.kpis.totalPT ?? '—'}
              <span className="text-[11px] font-normal text-teal-400 ml-1.5">
                ({data?.kpis.activePT ?? 0} activos)
              </span>
            </p>
          </div>
          <Package className="w-6 h-6 text-teal-400/70" />
        </div>

        <div className="bg-page border border-borderSubtle rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Recetas BOM (mrp.bom)
            </p>
            <p className="text-lg font-black text-emerald-400 mt-0.5">
              {data?.kpis.withBOMCount ?? '—'}
              <span className="text-[11px] font-normal text-slate-400 ml-1.5">
                con explosión · {data?.kpis.withoutBOMCount ?? 0} sin BOM
              </span>
            </p>
          </div>
          <Scale className="w-6 h-6 text-emerald-400/70" />
        </div>

        <div className="bg-page border border-borderSubtle rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Juegos Compuestos 4-UP
            </p>
            <p className="text-lg font-black text-indigo-300 mt-0.5">
              {data?.kpis.compositeKitsCount ?? data?.kpis.kitsModularesCount ?? '—'}
              <span className="text-[11px] font-normal text-indigo-400 ml-1.5">
                sets multi-bulto &le;80ch · {data?.kpis.kitsModularesCount ?? 0} mod.
              </span>
            </p>
          </div>
          <Layers className="w-6 h-6 text-indigo-400/70" />
        </div>

        <div className="bg-page border border-borderSubtle rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Insumos / Fijación Obra
            </p>
            <p className="text-lg font-black text-amber-300 mt-0.5">
              {data?.kpis.requiereFijacionCount ?? '—'}
              <span className="text-[11px] font-normal text-amber-400 ml-1.5">
                anclajes · {data?.kpis.linkedNotionInsumosCount ?? data?.kpis.totalMaterialsRef ?? 0} insumos Notion
              </span>
            </p>
          </div>
          <Hammer className="w-6 h-6 text-amber-400/70" />
        </div>
      </div>

      {/* Barra de Filtros y Búsqueda del Catálogo PT */}
      <div className="p-3 bg-page border border-borderSubtle rounded-xl flex flex-wrap items-center justify-between gap-2.5">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar mueble por código (P940, P862), nombre o insumo BOM..."
            className="w-full pl-9 pr-3 py-1.5 text-xs bg-surface border border-borderSubtle rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-teal-400"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={familyFilter}
            onChange={e => setFamilyFilter(e.target.value)}
            className="px-2.5 py-1.5 text-xs bg-surface border border-borderSubtle rounded-lg text-slate-200 focus:outline-none focus:border-teal-400"
          >
            <option value="TODAS">Todas las Familias ({items.length})</option>
            {PT_FAMILIES.map(f => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-1 overflow-x-auto">
            {[
              { id: 'ALL', label: `Todos (${items.length})` },
              { id: 'WITH_BOM', label: `Con BOM (${data?.kpis.withBOMCount ?? 0})` },
              { id: 'WITHOUT_BOM', label: `Sin BOM (${data?.kpis.withoutBOMCount ?? 0})` },
              { id: 'KITS_4UP', label: `Juegos 4-UP (${data?.kpis.compositeKitsCount ?? 33})` },
              { id: 'PARAMETRIC', label: '📐 Paramétricos / SERV (ML·MT2)' },
              { id: 'FIXATION', label: `Fijación (${data?.kpis.requiereFijacionCount ?? 0})` }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setModeFilter(tab.id as any)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition ${
                  modeFilter === tab.id
                    ? 'bg-teal-500 text-slate-950'
                    : 'bg-surface text-slate-400 hover:text-slate-200 border border-borderSubtle'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <button
            onClick={openCreatePT}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 border border-teal-500/40 transition"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Nuevo Mueble PT</span>
          </button>

          <button
            onClick={() => fetchCatalog(true)}
            disabled={loading}
            className="p-1.5 rounded-lg bg-surface hover:bg-surfaceHighest text-slate-300 border border-borderSubtle transition"
            title="Refrescar Catálogo PT y Recetas BOM desde Notion ERP"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-teal-400' : ''}`} />
          </button>
        </div>
      </div>

      {/* Estado de Carga o Error */}
      {loading && !data ? (
        <div className="py-14 flex flex-col items-center justify-center gap-3 text-slate-400">
          <Loader2 className="w-7 h-7 animate-spin text-teal-400" />
          <p className="text-xs font-medium">
            Hidratando 1.282 Muebles PT únicos, 960 Recetas BOM y 442 Insumos Notion...
          </p>
        </div>
      ) : errorMsg ? (
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-200 flex items-center justify-between text-xs">
          <span>{errorMsg}</span>
          <button
            onClick={() => fetchCatalog(true)}
            className="px-3 py-1 rounded-lg bg-rose-500/20 border border-rose-500/40 font-semibold"
          >
            Reintentar
          </button>
        </div>
      ) : (
        <>
          {/* Grid de Muebles PT */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filteredItems.slice(0, visibleLimit).map(item => (
              <div
                key={item.id}
                className="bg-page border border-borderSubtle hover:border-teal-500/40 rounded-xl p-4 flex flex-col justify-between gap-3 transition"
              >
                <div className="space-y-2">
                  {/* Encabezado de Tarjeta PT */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="px-2 py-0.5 rounded bg-teal-500/20 text-teal-300 font-mono text-xs font-black border border-teal-500/40">
                          {item.codigo}
                        </span>
                        <span className="px-2 py-0.5 rounded bg-surface text-slate-300 text-[10px] font-semibold border border-borderSubtle">
                          {item.familia}
                        </span>
                        <span
                          className={`px-1.5 py-0.5 rounded font-mono text-[10px] ${
                            ['METROS', 'ML', 'MT2', 'M2'].includes((item.unidadMedida || '').toUpperCase())
                              ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 font-bold'
                              : item.codigo.startsWith('SERV-') || item.unidadMedida === 'SERV'
                                ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 font-bold'
                                : 'bg-surface text-slate-400'
                          }`}
                        >
                          {['METROS', 'ML', 'MT2', 'M2'].includes((item.unidadMedida || '').toUpperCase())
                            ? `📐 Base: 1 ${item.unidadMedida === 'METROS' ? 'ML' : item.unidadMedida}`
                            : item.unidadMedida}
                        </span>
                        {item.pedidosCount > 0 && (
                          <span className="px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 text-[10px] font-mono border border-indigo-500/30">
                            {item.pedidosCount} ped.
                          </span>
                        )}
                      </div>
                      <h4 className="text-xs font-bold text-white mt-1.5 leading-snug line-clamp-2">
                        {item.nombre}
                      </h4>
                    </div>

                    <button
                      onClick={() => openEditPT(item)}
                      className="p-1.5 rounded-lg bg-surface hover:bg-surfaceHighest text-slate-300 border border-borderSubtle shrink-0"
                      title="Editar Ficha Técnica, Sustratos y Desglose 4-UP"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Estado de Receta BOM y Costo Teórico */}
                  <div className="p-2.5 rounded-lg bg-surface border border-borderSubtle flex items-center justify-between gap-2">
                    {item.hasBOM ? (
                      <div>
                        <div className="flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                          <span className="text-xs font-bold text-emerald-300">
                            {item.insumosCount} insumos en BOM
                          </span>
                          <span className="text-[10px] px-1.5 py-0.2 rounded bg-emerald-500/15 text-emerald-300 font-mono">
                            {item.bomSource === 'kv_custom'
                              ? 'Custom KV'
                              : item.bomSource === 'notion_bom'
                              ? 'Notion BOM'
                              : item.bomSource === 'hybrid'
                              ? 'Híbrido'
                              : 'Valery BOM'}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          Costo Teórico Materiales:{' '}
                          <strong className="text-white font-mono">${item.costoTeoricoUSD.toFixed(2)} USD</strong>
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                        <div>
                          <p className="text-xs font-bold text-amber-300">Sin Receta BOM Cuantitativa</p>
                          <p className="text-[10px] text-slate-400">
                            Usa el Constructor/Clonador para asignarle materiales
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Chips de Ingeniería de Fijación, Operaciones Manufactura y Juego Compuesto 4-UP */}
                  <div className="flex flex-wrap items-center gap-1.5">
                    {item.requiereFijacion && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/10 text-amber-300 text-[10px] border border-amber-500/30">
                        <Hammer className="w-3 h-3" />
                        {item.tipoCargaFijacion} · Pared: {item.puntosPared} / Piso: {item.puntosPiso}
                      </span>
                    )}
                    {item.isTrueCompositeKit ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-200 font-bold text-[10px] border border-indigo-400/50">
                        <Layers className="w-3 h-3 text-indigo-300" />
                        🧩 Juego Compuesto 4-UP ({item.empaqueNominal || 1} bultos)
                      </span>
                    ) : (
                      item.esKitModular && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-surface text-slate-400 text-[10px] border border-borderSubtle">
                          <Layers className="w-3 h-3" />
                          Modular ({item.empaqueNominal || 1} bulto)
                        </span>
                      )
                    )}
                    {item.operacionesNombres &&
                      item.operacionesNombres.slice(0, 2).map((opName, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-sky-500/10 text-sky-300 text-[10px] border border-sky-500/30"
                        >
                          ⚙️ {opName}
                        </span>
                      ))}
                  </div>

                  {/* Vista previa de Desglose 4-UP (<=80 chars) si es Juego Compuesto o tiene SubKit */}
                  {(item.subKitEnsamble || item.suggestedSubKit4UP) && (
                    <div className="p-2 rounded-lg bg-indigo-950/30 border border-indigo-500/30 space-y-1">
                      <p className="text-[10px] font-bold text-indigo-300 uppercase tracking-wider flex items-center justify-between">
                        <span>Etiquetas Térmicas 4-UP (&le;80 chars)</span>
                        <span className="font-mono text-[9px] text-emerald-300">PROTEGIDO</span>
                      </p>
                      {(item.subKitEnsamble || item.suggestedSubKit4UP || '')
                        .split(/\r?\n|;/)
                        .map(s => s.trim())
                        .filter(Boolean)
                        .slice(0, 3)
                        .map((ln, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between gap-2 text-[10px] font-mono text-indigo-100 bg-page/60 px-2 py-0.5 rounded"
                          >
                            <span className="truncate">{ln}</span>
                            <span className="text-emerald-400 shrink-0">{ln.length}/80ch</span>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Vista previa rápida de los primeros 4 insumos BOM */}
                  {item.hasBOM && (
                    <div className="flex flex-wrap gap-1 pt-0.5">
                      {item.bomLines.slice(0, 4).map((l, idx) => (
                        <span
                          key={`${l.matCode}-${idx}`}
                          className="px-1.5 py-0.5 rounded bg-page text-slate-300 font-mono text-[10px] border border-borderSubtle truncate max-w-[170px]"
                          title={`${l.nombre}: ${l.cantidadBase} ${l.unidad}`}
                        >
                          {l.matCode}: {l.cantidadBase}
                        </span>
                      ))}
                      {item.bomLines.length > 4 && (
                        <span className="px-1.5 py-0.5 rounded bg-surface text-slate-400 font-mono text-[10px]">
                          +{item.bomLines.length - 4} más
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Botonera Inferior: Inspector BOM + Clonador a Medida */}
                <div className="pt-2.5 border-t border-borderSubtle flex items-center justify-between gap-2">
                  <button
                    onClick={() => openInspectEditBOM(item)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-surface hover:bg-surfaceHighest text-teal-300 border border-teal-500/30 transition"
                  >
                    <Wrench className="w-3.5 h-3.5" />
                    <span>{item.hasBOM ? 'Ver / Editar BOM' : 'Construir BOM'}</span>
                  </button>

                  <button
                    onClick={() => openCloneFurnitureWithBOM(item)}
                    className="flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30 transition"
                    title="Clonar este mueble y su receta BOM para crear un mueble a medida"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    <span>Clonar</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredItems.length > visibleLimit && (
            <div className="pt-2 flex justify-center">
              <button
                onClick={() => setVisibleLimit(prev => prev + 48)}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-surfaceHigh hover:bg-surfaceHighest text-teal-300 border border-teal-500/30 transition"
              >
                Mostrar más muebles PT (+48) — Mostrando {visibleLimit} de {filteredItems.length}
              </button>
            </div>
          )}
        </>
      )}

      {/* =====================================================================
          MODAL 1: CONSTRUCTOR / CLONADOR INTERACTIVO DE RECETA BOM (mrp.bom)
         ===================================================================== */}
      {bomModal.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 overflow-y-auto">
          <div className="bg-surface border border-borderSubtle rounded-2xl w-full max-w-4xl overflow-hidden shadow-2xl my-auto">
            <div className="px-5 py-3.5 bg-surfaceHigh border-b border-borderSubtle flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Sparkles className="w-5 h-5 text-amber-400" />
                <div>
                  <h3 className="text-sm font-bold text-white">
                    {bomModal.mode === 'clone_new'
                      ? `Clonador de Mueble a Medida y Receta BOM (Base: ${bomModal.baseItem?.codigo})`
                      : `Ingeniería de Receta BOM — ${bomModal.baseItem?.codigo}: ${bomModal.baseItem?.nombre}`}
                  </h3>
                  <p className="text-[11px] text-slate-400">
                    Odoo 18 mrp.bom (Base: 1 {bomModal.baseItem?.unidadMedida || ptForm.unidadMedida || 'UND'}) · Costo
                    Unitario Base:{' '}
                    <strong className="text-emerald-400 font-mono">${totalEditableBomCostUSD.toFixed(2)} USD</strong>{' '}
                    ({editableBomLines.length} insumos) · Simulado ({scaleFactor}x):{' '}
                    <strong className="text-cyan-300 font-mono">
                      ${(totalEditableBomCostUSD * (Number(scaleFactor) || 1)).toFixed(2)} USD
                    </strong>
                  </p>
                </div>
              </div>
              <button
                onClick={() => setBomModal({ open: false, mode: 'inspect_edit', baseItem: null })}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveBomModal} className="p-5 space-y-4 max-h-[80vh] overflow-y-auto text-xs">
              {/* Si estamos clonando hacia un nuevo mueble a medida, mostrar datos del nuevo PT */}
              {bomModal.mode === 'clone_new' && (
                <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[11px] text-amber-300 font-bold mb-1">
                      Nuevo Código PT (Variante) *
                    </label>
                    <input
                      type="text"
                      required
                      value={ptForm.codigo}
                      onChange={e => setPtForm(p => ({ ...p, codigo: e.target.value.toUpperCase() }))}
                      className="w-full px-3 py-1.5 rounded-lg bg-page border border-borderSubtle text-white font-mono"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-[11px] text-amber-300 font-bold mb-1">
                      Nombre Descriptivo del Mueble a Medida *
                    </label>
                    <input
                      type="text"
                      required
                      value={ptForm.nombre}
                      onChange={e => setPtForm(p => ({ ...p, nombre: e.target.value }))}
                      className="w-full px-3 py-1.5 rounded-lg bg-page border border-borderSubtle text-white"
                    />
                  </div>
                </div>
              )}

              {/* Barra de Asistencia Rápida: Cargar plantilla desde otro mueble + Escalador dimensional */}
              <div className="p-3 rounded-xl bg-page border border-borderSubtle flex flex-wrap items-end justify-between gap-3">
                <div className="flex-1 min-w-[260px] space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <label className="block text-[11px] text-slate-400 font-semibold">
                      Copiar / Precargar desde las {itemsWithBom.length} recetas existentes ({filteredTemplates.length} filtradas):
                    </label>
                    <input
                      type="text"
                      value={templateSearch}
                      onChange={e => setTemplateSearch(e.target.value)}
                      placeholder="Filtrar plantilla (ej. P940, VITRINA, CAJA)..."
                      className="w-52 px-2 py-0.5 rounded bg-surface border border-borderSubtle text-[11px] text-white placeholder-slate-500"
                    />
                  </div>
                  <select
                    value={cloneSourceCode}
                    onChange={e => handleLoadRecipeFromTemplate(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-surface border border-borderSubtle text-slate-200"
                  >
                    <option value="">-- Seleccionar cualquiera de los {filteredTemplates.length} muebles con BOM --</option>
                    {filteredTemplates.map(it => (
                      <option key={it.id} value={it.codigo}>
                        {it.codigo} — {it.nombre.slice(0, 68)} ({it.insumosCount} ins. · ${it.costoTeoricoUSD})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end gap-2">
                  <div>
                    <label className="block text-[11px] text-slate-400 font-semibold mb-1">
                      Escala / Medida ({bomModal.baseItem?.unidadMedida || 'UND/ML/MT2'})
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.1"
                      max="1000"
                      value={scaleFactor}
                      onChange={e => setScaleFactor(e.target.value)}
                      className="w-24 px-2.5 py-1.5 rounded-lg bg-surface border border-borderSubtle text-white font-mono"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleApplyScaleFactor}
                    className="px-3 py-1.5 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 border border-indigo-500/40 font-semibold"
                  >
                    Escalar Cantidades ({scaleFactor}x)
                  </button>
                </div>
              </div>

              {/* Tabla de Líneas BOM Editables */}
              <div className="border border-borderSubtle rounded-xl overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-surfaceHigh text-[10px] uppercase text-slate-400 border-b border-borderSubtle">
                      <th className="py-2 px-3">Código / SKU Notion</th>
                      <th className="py-2 px-3">Insumo / Stock Almacén</th>
                      <th className="py-2 px-3">Etapa Productiva</th>
                      <th className="py-2 px-3 text-right">Cant. Base</th>
                      <th className="py-2 px-3 text-right">% Merma</th>
                      <th className="py-2 px-3 text-right">Costo Unit.</th>
                      <th className="py-2 px-3 text-right">Subtotal</th>
                      <th className="py-2 px-2 text-center">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-borderSubtle bg-page">
                    {editableBomLines.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-slate-500 italic">
                          Sin insumos cargados. Selecciona una plantilla arriba o agrega un insumo abajo.
                        </td>
                      </tr>
                    ) : (
                      editableBomLines.map((line, idx) => {
                        const matInfo = materialsByCode.get(String(line.matCode || '').toUpperCase());
                        const stockDisp = matInfo?.stockBase ?? 0;
                        return (
                          <tr key={`${line.matCode}-${idx}`} className="hover:bg-surface/60">
                            <td className="py-2 px-3 font-mono font-bold text-teal-300">
                              {line.matCode}
                              {line.sku && line.sku !== line.matCode && (
                                <span className="block text-[10px] text-slate-400 font-normal">{line.sku}</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-slate-200">
                              {line.nombre}
                              <span className="block text-[10px] text-slate-400 font-mono">
                                {line.unidad} · Stock Almacén:{' '}
                                <strong className={stockDisp > 0 ? 'text-emerald-300' : 'text-amber-300'}>
                                  {stockDisp}
                                </strong>
                              </span>
                            </td>
                            <td className="py-2 px-3">
                              <select
                                value={line.etapaProductiva}
                                onChange={e => handleUpdateBomLine(idx, 'etapaProductiva', e.target.value)}
                                className="px-2 py-1 rounded bg-surface border border-borderSubtle text-slate-200 text-[11px]"
                              >
                                {BOM_PRODUCTION_STAGES.map(st => (
                                  <option key={st} value={st}>
                                    {st}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="py-2 px-3 text-right">
                              <input
                                type="number"
                                step="0.001"
                                min="0.0001"
                                value={line.cantidadBase}
                                onChange={e => handleUpdateBomLine(idx, 'cantidadBase', e.target.value)}
                                className="w-20 px-2 py-1 rounded bg-surface border border-borderSubtle text-right text-white font-mono"
                              />
                            </td>
                            <td className="py-2 px-3 text-right">
                              <input
                                type="number"
                                step="1"
                                min="0"
                                max="100"
                                value={line.mermaPct}
                                onChange={e => handleUpdateBomLine(idx, 'mermaPct', e.target.value)}
                                className="w-16 px-2 py-1 rounded bg-surface border border-borderSubtle text-right text-amber-300 font-mono"
                              />
                            </td>
                            <td className="py-2 px-3 text-right font-mono text-slate-400">
                              ${line.costoUnitarioUSD.toFixed(2)}
                            </td>
                            <td className="py-2 px-3 text-right font-mono font-bold text-emerald-300">
                              ${line.subtotalUSD.toFixed(2)}
                            </td>
                            <td className="py-2 px-2 text-center">
                              <button
                                type="button"
                                onClick={() => handleRemoveBomLine(idx)}
                                className="p-1 text-slate-500 hover:text-rose-400"
                                title="Quitar insumo de la receta"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Agregar Nuevo Insumo Maestro a la Receta */}
              <div className="p-3 rounded-xl bg-page border border-borderSubtle grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                <div className="sm:col-span-5 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <label className="block text-[10px] uppercase text-slate-400 font-semibold">
                      Añadir Insumo Notion ({filteredMaterialsCatalog.length}/{materialsCatalog.length} ref.)
                    </label>
                    <input
                      type="text"
                      value={matSearch}
                      onChange={e => setMatSearch(e.target.value)}
                      placeholder="Buscar SKU o nombre..."
                      className="w-40 px-2 py-0.5 rounded bg-surface border border-borderSubtle text-[10px] text-white"
                    />
                  </div>
                  <select
                    value={newMatCode}
                    onChange={e => setNewMatCode(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-surface border border-borderSubtle text-slate-200"
                  >
                    <option value="">-- Seleccionar Insumo (M* / SKU Almacén) --</option>
                    {filteredMaterialsCatalog.map(m => (
                      <option key={m.code} value={m.code}>
                        {m.code} ({m.sku}) — {m.nombre} [{m.unidad} · ${m.costoUSD} · Stock: {m.stockBase ?? 0}]
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-3">
                  <label className="block text-[10px] uppercase text-slate-400 font-semibold mb-1">
                    Etapa Productiva
                  </label>
                  <select
                    value={newMatStage}
                    onChange={e => setNewMatStage(e.target.value)}
                    className="w-full px-2 py-1.5 rounded-lg bg-surface border border-borderSubtle text-slate-200"
                  >
                    {BOM_PRODUCTION_STAGES.map(st => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-[10px] uppercase text-slate-400 font-semibold mb-1">
                    Cant. Base
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.001"
                    value={newMatQty}
                    onChange={e => setNewMatQty(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-surface border border-borderSubtle text-white font-mono"
                  />
                </div>

                <div className="sm:col-span-2">
                  <button
                    type="button"
                    onClick={handleAddMaterialToBom}
                    disabled={!newMatCode}
                    className="w-full py-1.5 px-3 rounded-lg bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 border border-teal-500/40 font-bold disabled:opacity-40"
                  >
                    + Insumo
                  </button>
                </div>
              </div>

              {/* Autorización y Guardado Transaccional */}
              <div className="pt-3 border-t border-borderSubtle flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-[11px] text-slate-400">PIN Supervisor (respaldo):</label>
                  <input
                    type="password"
                    value={supervisorPIN}
                    onChange={e => setSupervisorPIN(e.target.value)}
                    placeholder="PIN o JWT activo"
                    className="w-32 px-2.5 py-1 rounded-lg bg-page border border-borderSubtle text-white font-mono text-xs"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setBomModal({ open: false, mode: 'inspect_edit', baseItem: null })}
                    className="px-4 py-2 rounded-xl bg-page text-slate-300 border border-borderSubtle"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="flex items-center gap-2 px-5 py-2 rounded-xl font-bold bg-teal-500 hover:bg-teal-400 text-slate-950 shadow-lg transition disabled:opacity-50"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    <span>
                      {bomModal.mode === 'clone_new'
                        ? 'Crear Mueble Clonado + Guardar BOM en Notion'
                        : 'Guardar Receta BOM en Notion ERP'}
                    </span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =====================================================================
          MODAL 2: EDITOR DE FICHA TÉCNICA PT, SUSTRATOS Y DESGLOSE 4-UP (<=80 CHARS)
         ===================================================================== */}
      {ptEditorModal.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 overflow-y-auto">
          <div className="bg-surface border border-borderSubtle rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl my-auto">
            <div className="px-5 py-3.5 bg-surfaceHigh border-b border-borderSubtle flex items-center justify-between">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Package className="w-4 h-4 text-teal-400" />
                <span>
                  {ptEditorModal.action === 'create_pt'
                    ? 'Registrar Nuevo Mueble en BD_Catalogo_Mobiliario_PT'
                    : `Editar Ficha Técnica PT — ${ptForm.codigo}`}
                </span>
              </h3>
              <button
                onClick={() => setPtEditorModal({ open: false, action: 'create_pt' })}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSavePTForm} className="p-5 space-y-4 text-xs max-h-[80vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] text-slate-400 mb-1">Código PT *</label>
                  <input
                    type="text"
                    required
                    value={ptForm.codigo}
                    onChange={e => setPtForm(p => ({ ...p, codigo: e.target.value.toUpperCase() }))}
                    className="w-full px-3 py-2 rounded-lg bg-page border border-borderSubtle text-white font-mono"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] text-slate-400 mb-1">Nombre del Producto Terminado *</label>
                  <input
                    type="text"
                    required
                    value={ptForm.nombre}
                    onChange={e => setPtForm(p => ({ ...p, nombre: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-page border border-borderSubtle text-white"
                  />
                </div>

                <div>
                  <label className="block text-[11px] text-slate-400 mb-1">Familia / Categoría</label>
                  <select
                    value={ptForm.familia}
                    onChange={e => setPtForm(p => ({ ...p, familia: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-page border border-borderSubtle text-white"
                  >
                    {PT_FAMILIES.map(f => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] text-slate-400 mb-1">Unidad de Medida</label>
                  <select
                    value={ptForm.unidadMedida}
                    onChange={e => setPtForm(p => ({ ...p, unidadMedida: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-page border border-borderSubtle text-white"
                  >
                    {PT_UNITS.map(u => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] text-slate-400 mb-1">Estatus</label>
                  <select
                    value={ptForm.estatus}
                    onChange={e => setPtForm(p => ({ ...p, estatus: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-page border border-borderSubtle text-white"
                  >
                    {PT_STATUSES.map(s => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Bloque de Ingeniería de Fijación en Obra */}
              <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase text-amber-300 flex items-center gap-1.5">
                    <Hammer className="w-3.5 h-3.5" />
                    Ingeniería de Fijación y Anclaje en Obra
                  </span>
                  <label className="flex items-center gap-2 text-slate-200 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ptForm.requiereFijacion}
                      onChange={e => setPtForm(p => ({ ...p, requiereFijacion: e.target.checked }))}
                    />
                    <span>Requiere Fijación en Tienda</span>
                  </label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[11px] text-slate-300 mb-1">Tipo Carga Fijación</label>
                    <select
                      value={ptForm.tipoCargaFijacion}
                      onChange={e => setPtForm(p => ({ ...p, tipoCargaFijacion: e.target.value }))}
                      className="w-full px-2.5 py-1.5 rounded-lg bg-page border border-borderSubtle text-white"
                    >
                      {PT_FIXATION_LOADS.map(fl => (
                        <option key={fl} value={fl}>
                          {fl}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-300 mb-1">Puntos Pared</label>
                    <input
                      type="number"
                      min="0"
                      value={ptForm.puntosPared}
                      onChange={e => setPtForm(p => ({ ...p, puntosPared: Number(e.target.value) || 0 }))}
                      className="w-full px-2.5 py-1.5 rounded-lg bg-page border border-borderSubtle text-white font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-300 mb-1">Puntos Piso</label>
                    <input
                      type="number"
                      min="0"
                      value={ptForm.puntosPiso}
                      onChange={e => setPtForm(p => ({ ...p, puntosPiso: Number(e.target.value) || 0 }))}
                      className="w-full px-2.5 py-1.5 rounded-lg bg-page border border-borderSubtle text-white font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Bloque de Desglose de Kit Modular y Protección Térmica 4-UP (<=80 chars) */}
              <div className="p-3.5 rounded-xl bg-indigo-500/10 border border-indigo-500/30 space-y-2.5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="text-[11px] font-bold uppercase text-indigo-300 flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5" />
                    Desglose de Kit Modular y Etiquetado Térmico 4-UP (&le;80 chars)
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const gen = buildSuggested4UPBreakdown(ptForm.codigo, ptForm.nombre);
                        if (gen) {
                          setPtForm(p => ({ ...p, esKitModular: true, subKitEnsamble: gen }));
                        }
                      }}
                      className="px-2.5 py-1 rounded-md bg-indigo-500/25 hover:bg-indigo-500/40 text-indigo-200 border border-indigo-400/50 text-[10px] font-bold flex items-center gap-1"
                    >
                      <Sparkles className="w-3 h-3 text-amber-300" />
                      <span>Autogenerar Desglose 4-UP (&le;80 chars)</span>
                    </button>
                    <label className="flex items-center gap-2 text-slate-200 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={ptForm.esKitModular}
                        onChange={e => setPtForm(p => ({ ...p, esKitModular: e.target.checked }))}
                      />
                      <span>Es Kit Modular / Juego Compuesto</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] text-slate-300 mb-1">
                    SubKit A Ensamble (1 componente por línea — Máximo 80 caracteres por etiqueta 4-UP)
                  </label>
                  <textarea
                    rows={3}
                    value={ptForm.subKitEnsamble}
                    onChange={e => setPtForm(p => ({ ...p, subKitEnsamble: e.target.value }))}
                    placeholder="Ej: MESA CENTRAL 75X180X80CM&#10;TARIMA LATERAL CON PUERTA 50X160X45CM"
                    className="w-full px-3 py-2 rounded-lg bg-page border border-borderSubtle text-white font-mono text-xs"
                  />
                </div>

                {subKitValidation.lines.length > 0 && (
                  <div className="space-y-1">
                    {subKitValidation.lines.map((ln, i) => (
                      <div
                        key={i}
                        className={`px-2.5 py-1 rounded flex items-center justify-between text-[11px] font-mono ${
                          ln.ok
                            ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                            : 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                        }`}
                      >
                        <span className="truncate mr-2">{ln.text}</span>
                        <span className="shrink-0 font-bold">{ln.len}/80 chars</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="pt-3 border-t border-borderSubtle flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-400">PIN Supervisor:</span>
                  <input
                    type="password"
                    value={supervisorPIN}
                    onChange={e => setSupervisorPIN(e.target.value)}
                    placeholder="Opcional si JWT"
                    className="w-28 px-2.5 py-1 rounded-lg bg-page border border-borderSubtle text-white font-mono"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPtEditorModal({ open: false, action: 'create_pt' })}
                    className="px-4 py-2 rounded-xl bg-page text-slate-300 border border-borderSubtle"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving || !subKitValidation.valid}
                    className="flex items-center gap-1.5 px-5 py-2 rounded-xl font-bold bg-teal-500 hover:bg-teal-400 text-slate-950 transition disabled:opacity-50"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                    <span>Guardar en Notion ERP</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
